import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { newsAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { ArrowLeft, Calendar, User, Share2, Tag, ArrowRight, Camera } from 'lucide-react';

const NewsDetailContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  width: 100%;
`;

const BackButton = styled(Button)`
  margin-bottom: 2rem;
`;

const ArticleCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
`;

const NewsImage = styled.div`
  width: 100%;
  height: 400px;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const NewsContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.85rem;
  background-color: ${props => {
    const category = props.category?.toLowerCase();
    if (category === 'event') return '#FF6B35';
    if (category === 'announcement') return '#4ECDC4';
    if (category === 'success story') return '#95E1D3';
    if (category === 'press release') return '#F38181';
    return props.theme.colors.primary;
  }};
  color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-start;
  margin-bottom: 0.75rem;
`;

const ArticleTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.75rem 0;
  line-height: 1.4;
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border || '#e5e5e5'};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.xs};
`;

const NewsExcerpt = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
`;

const ArticleContent = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.7;
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  h2 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
    margin: 2rem 0 1rem 0;
    font-family: ${props => props.theme.typography.fontFamily.heading};
  }
  
  h3 {
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.text.primary};
    margin: 1.5rem 0 0.75rem 0;
    font-family: ${props => props.theme.typography.fontFamily.heading};
  }
  
  p {
    margin-bottom: 1.5rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  blockquote {
    border-left: 4px solid ${props => props.theme.colors.primary};
    padding-left: 1.5rem;
    margin: 2rem 0;
    font-style: italic;
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const ArticleActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.border || '#e5e5e5'};
`;

const RelatedArticles = styled.div`
  margin-top: 3rem;
`;

const RelatedTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin-bottom: 1.5rem;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  gap: 1.5rem;
  }
`;

const RelatedCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  height: 100%;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const RelatedCardImage = styled.div`
  width: 100%;
  height: 240px;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.08);
  }
`;

const RelatedCardContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const RelatedCardCategory = styled.span`
  display: inline-block;
  padding: 0.35rem 0.85rem;
  background-color: ${props => {
    const category = props.category?.toLowerCase();
    if (category === 'event') return '#FF6B35';
    if (category === 'announcement') return '#4ECDC4';
    if (category === 'success story') return '#95E1D3';
    if (category === 'press release') return '#F38181';
    return props.theme.colors.primary;
  }};
  color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-start;
`;

const RelatedCardTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RelatedCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const RelatedExcerpt = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const RelatedReadMore = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: none;
  padding: 0.75rem 0;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  align-self: flex-start;
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    gap: 0.75rem;
    
    svg {
      transform: translateX(4px);
    }
  }
  
  svg {
    transition: transform 0.3s ease;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
`;

const NewsDetail = () => {
  const { theme } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    fetchNews();
    fetchRelatedNews();
  }, [id]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getById(id);
      setArticle(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedNews = async () => {
    try {
      const response = await newsAPI.getRecent(3);
      setRelatedArticles(response.data.filter(news => news.id !== parseInt(id)));
    } catch (err) {
      console.error('Error fetching related news:', err);
    }
  };

  const handleShare = () => {
    if (article && navigator.share) {
      navigator.share({
        title: article.title,
        text: article.summary || article.excerpt,
        url: window.location.href
      });
    } else if (article) {
      navigator.clipboard.writeText(window.location.href);
      alert('Article link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <NewsDetailContainer>
        <PublicHeader theme={theme} />
        <LoadingState>Loading article...</LoadingState>
        <PublicFooter theme={theme} />
      </NewsDetailContainer>
    );
  }

  if (error || !article) {
    return (
      <NewsDetailContainer>
        <PublicHeader theme={theme} />
        <Container>
          <Card padding="3rem" textAlign="center">
            <h1>Article Not Found</h1>
            <p>{error || 'The requested article could not be found.'}</p>
            <Button variant="primary" onClick={() => navigate('/news')}>
              Back to News
            </Button>
          </Card>
        </Container>
        <PublicFooter theme={theme} />
      </NewsDetailContainer>
    );
  }

  return (
    <NewsDetailContainer>
      <PublicHeader theme={theme} />
      
      <Container>
        <BackButton 
          variant="outline" 
          leftIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/news')}
        >
          Back to News
        </BackButton>

        <ArticleCard>
          {/* Image First - Like Landing Page */}
          <NewsImage>
            {article.featured_image_url ? (
              <img 
                src={article.featured_image_url.startsWith('http') 
                  ? article.featured_image_url 
                  : getStorageUrl(article.featured_image_url)} 
                alt={article.title}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <Camera size={64} style={{ opacity: 0.2 }} />
            )}
          </NewsImage>

          {/* Content Section - Like Landing Page NewsContent */}
          <NewsContent>
            {/* Category Badge */}
              {article.category && (
                <CategoryBadge category={article.category}>
                  {article.category}
                </CategoryBadge>
              )}

            {/* Tags */}
              {article.tags && article.tags.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '-0.5rem' }}>
                  {article.tags.map((tag, index) => (
                  <span key={index} style={{ fontSize: '0.75rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Tag size={12} />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

            {/* Title */}
            <ArticleTitle>{article.title}</ArticleTitle>

            {/* Meta Information */}
            <ArticleMeta>
              <MetaItem>
                <Calendar size={12} />
                {new Date(article.published_date || article.created_at).toLocaleDateString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </MetaItem>
              <MetaItem>
                <User size={12} />
                {article.author?.full_name || article.author?.fullName || 'EWHRDN'}
              </MetaItem>
            </ArticleMeta>

            {/* Summary/Excerpt */}
          {article.summary && (
              <NewsExcerpt>
                {article.summary}
              </NewsExcerpt>
            )}

            {/* Full Content */}
            {article.content && (
          <ArticleContent dangerouslySetInnerHTML={{ __html: article.content }} />
            )}

            {/* Share Button */}
          <ArticleActions>
            <Button variant="outline" leftIcon={<Share2 size={16} />} onClick={handleShare}>
              Share Article
            </Button>
          </ArticleActions>
          </NewsContent>
        </ArticleCard>

        {relatedArticles.length > 0 && (
        <RelatedArticles>
          <RelatedTitle>Related Articles</RelatedTitle>
          <RelatedGrid>
            {relatedArticles.map((related) => (
              <RelatedCard 
                key={related.id}
                onClick={() => navigate(`/news/${related.id}`)}
              >
                <RelatedCardImage>
                  {related.featured_image_url ? (
                    <img 
                      src={related.featured_image_url.startsWith('http') 
                        ? related.featured_image_url 
                        : getStorageUrl(related.featured_image_url)}
                      alt={related.title}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Camera size={64} style={{ opacity: 0.2 }} />
                  )}
                </RelatedCardImage>
                
                <RelatedCardContent>
                  {related.category && (
                    <RelatedCardCategory category={related.category}>
                      {related.category.replace('_', ' ')}
                    </RelatedCardCategory>
                  )}
                  
                <RelatedCardTitle>{related.title}</RelatedCardTitle>
                  
                  <RelatedCardMeta>
                    <span>
                      <Calendar size={12} />
                      {new Date(related.published_date || related.created_at).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span>
                      <User size={12} />
                      {related.author?.full_name || related.author?.fullName || 'EWHRDN'}
                    </span>
                  </RelatedCardMeta>
                  
                  <RelatedExcerpt>
                    {related.summary || related.excerpt || related.content?.substring(0, 120) + '...'}
                  </RelatedExcerpt>
                  
                  <RelatedReadMore onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/news/${related.id}`);
                  }}>
                    Read More <ArrowRight size={16} />
                  </RelatedReadMore>
                </RelatedCardContent>
              </RelatedCard>
            ))}
          </RelatedGrid>
        </RelatedArticles>
        )}
      </Container>
      
      <PublicFooter theme={theme} />
    </NewsDetailContainer>
  );
};

export default NewsDetail;
